import { rem } from 'polished'
import type { MouseEvent } from 'react'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import AssistChip from 'LuxKit/Chips/AssistChip'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import Modal from 'LuxKit/Modal/Modal'
import noop from 'lib/function/noop'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import { getCruiseDepartureSelectionInItinerary, getOfferItineraryDayRange } from 'tripPlanner/utils/chatItinerary'
import ChatCruiseDepartureModalContent from './ChatCruiseDepartureModalContent'
import {
  buildCruiseItineraryActionMessage,
  findDepartureBySelection,
  getCruiseDepartures,
  getCruiseRouteLabel,
  getTripDatesSummary,
} from './ChatCruiseAddToTripActionUtils'

export { buildCruiseItineraryActionMessage } from './ChatCruiseAddToTripActionUtils'

const EMPTY_CRUISE_ITINERARY_ITEMS: Array<App.CruiseItineraryItem> = []

interface Props {
  offerId: string
  cruise?: App.CruiseOffer
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  isStreaming?: boolean
}

const StatusAssistChip = styled(AssistChip)`
  max-width: ${rem(176)};

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

function ChatCruiseAddToTripAction({
  offerId,
  cruise,
  itineraryData,
  onSendMessage = noop,
  isStreaming = false,
}: Props) {
  const intl = useIntl()
  const [isDepartureModalOpen, setIsDepartureModalOpen] = useState(false)
  const [selectedDepartureId, setSelectedDepartureId] = useState<string | undefined>()
  const [expandedDepartureId, setExpandedDepartureId] = useState<string | undefined>()

  const addToTripLabel = intl.formatMessage({
    id: 'E_V466H8piA-_6ZGACfW',
    defaultMessage: 'Add to trip',
    description: 'Button label on cruise carousel cards that starts adding a cruise to the trip itinerary',
  })
  const chooseDepartureTitle = intl.formatMessage({
    id: 'bjgO3S-qgGCt77yYKApK',
    defaultMessage: 'Choose departure',
    description: 'Modal title shown when choosing which cruise departure to add to a trip itinerary',
  })
  const noDeparturesMessage = intl.formatMessage({
    id: 'eR0W3DJa5Ct7U9MYT51z',
    defaultMessage: 'Departure options could not be loaded for this cruise.',
    description: 'Unavailable state message shown when a cruise has no selectable departures to add to a trip',
  })
  const tripDatesHeading = intl.formatMessage({
    id: '9HJVop3bTk2XHr4Q9yCi',
    defaultMessage: 'Your trip dates',
    description: 'Section heading above the current trip date summary in the cruise departure modal',
  })
  const availableDeparturesHeading = intl.formatMessage({
    id: 'QFf3NYWkhdDB0z5gSMGl',
    defaultMessage: 'Available departures',
    description: 'Section heading above the selectable departure cards in the cruise departure modal',
  })
  const seeItineraryLabel = intl.formatMessage({
    id: 'pAhmXgX4gV6r33piKdsg',
    defaultMessage: 'See itinerary',
    description: 'Link label shown inside cruise departure cards to indicate itinerary details can be reviewed',
  })
  const atSeaLabel = intl.formatMessage({
    id: 'eglatGtb5bLey9o-rWTz',
    defaultMessage: 'At Sea',
    description: 'Itinerary label for days spent at sea during a cruise',
  })
  const fromLabel = intl.formatMessage({
    id: 'Pkbrv95sWyzNFJpk737U',
    defaultMessage: 'from',
    description: 'Short price caption shown before a cruise departure price',
  })
  const priceUnavailableLabel = intl.formatMessage({
    id: 'xCAzfNn6FaMvyloNdoH3',
    defaultMessage: 'Price unavailable',
    description: 'Fallback text shown when a cruise departure has no available price',
  })
  const toBePlannedLabel = intl.formatMessage({
    id: 'MgK5ISSCJKWklR8eg12e',
    defaultMessage: 'To be planned',
    description: 'Label for unassigned trip itinerary items that have no scheduled day',
  })
  const updateTripLabel = intl.formatMessage({
    id: 'qvyGfBvgd7DC0Dx3iS8u',
    defaultMessage: 'Update trip',
    description: 'Primary action button text when changing the selected cruise departure in a trip itinerary',
  })
  const cancelLabel = intl.formatMessage({
    id: 'vDsT2lvImnPffZkOoDfa',
    defaultMessage: 'Cancel',
    description: 'Secondary action button text to close the cruise departure selection modal',
  })

  const departures = useMemo(
    () => getCruiseDepartures(cruise),
    [cruise],
  )
  const itineraryItems = cruise?.itinerary ?? EMPTY_CRUISE_ITINERARY_ITEMS
  const tripDatesSummary = useMemo(
    () => getTripDatesSummary(intl, itineraryData),
    [intl, itineraryData],
  )
  const itineraryStatus = useMemo(
    () => getOfferItineraryDayRange(offerId, itineraryData),
    [offerId, itineraryData],
  )
  const itineraryDepartureSelection = useMemo(
    () => getCruiseDepartureSelectionInItinerary(offerId, itineraryData),
    [offerId, itineraryData],
  )
  const itineraryDeparture = useMemo(
    () => findDepartureBySelection(departures, itineraryDepartureSelection),
    [departures, itineraryDepartureSelection],
  )
  const routeLabel = useMemo(
    () => getCruiseRouteLabel(intl, cruise),
    [intl, cruise],
  )
  const selectedDeparture = useMemo(
    () => departures.find(departure => departure.id === selectedDepartureId),
    [departures, selectedDepartureId],
  )
  const modalLabels = useMemo(() => ({
    atSeaLabel,
    availableDeparturesHeading,
    fromLabel,
    noDeparturesMessage,
    priceUnavailableLabel,
    seeItineraryLabel,
    tripDatesHeading,
  }), [
    atSeaLabel,
    availableDeparturesHeading,
    fromLabel,
    noDeparturesMessage,
    priceUnavailableLabel,
    seeItineraryLabel,
    tripDatesHeading,
  ])

  useEffect(() => {
    setSelectedDepartureId(undefined)
    setExpandedDepartureId(undefined)
  }, [offerId])

  const formatItineraryDayLabel = useCallback((day: number) => intl.formatMessage(
    {
      id: 'ZXgIvBZ2-xbRyWFDcP9t',
      defaultMessage: 'Day {day}',
      description: 'Itinerary day number label in cruise itinerary modal',
    },
    { day },
  ), [intl])

  const handleOpenDepartures = useCallback(() => {
    setSelectedDepartureId(itineraryDeparture?.id)
    setIsDepartureModalOpen(true)
  }, [itineraryDeparture])

  const handleClose = useCallback(() => {
    setIsDepartureModalOpen(false)
  }, [])

  const handleDepartureClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setSelectedDepartureId(event.currentTarget.dataset.departureId)
  }, [])

  const handleItineraryToggle = useCallback((event: MouseEvent<HTMLElement>) => {
    const { departureId } = event.currentTarget.dataset
    setExpandedDepartureId(currentDepartureId => currentDepartureId === departureId ? undefined : departureId)
  }, [])

  const hasAddedCruise = !!itineraryStatus

  const handleAddSelectedDeparture = useCallback(() => {
    if (!cruise || !selectedDeparture) return
    onSendMessage(buildCruiseItineraryActionMessage({
      cruise,
      departure: selectedDeparture,
      action: hasAddedCruise ?
          { type: 'update', itemId: itineraryDepartureSelection?.itemId } :
          { type: 'add' },
      intl,
    }))
    handleClose()
  }, [cruise, handleClose, hasAddedCruise, intl, itineraryDepartureSelection?.itemId, onSendMessage, selectedDeparture])

  const actionLabel = hasAddedCruise ? toBePlannedLabel : addToTripLabel
  const actionTitle = hasAddedCruise ? routeLabel ?? itineraryStatus?.label ?? toBePlannedLabel : addToTripLabel
  const primaryActionText = hasAddedCruise ? updateTripLabel : addToTripLabel
  const ActionChip = hasAddedCruise ? StatusAssistChip : AssistChip

  return (
    <>
      <ActionChip
        variant="filled"
        size="small"
        startIcon={hasAddedCruise ? undefined : <LinePlusIcon />}
        endIcon={hasAddedCruise ? <LineAngleDownIcon /> : undefined}
        onClick={handleOpenDepartures}
        disabled={isStreaming}
        aria-label={actionLabel}
        title={actionTitle}
      >
        {actionLabel}
      </ActionChip>
      <Modal
        title={chooseDepartureTitle}
        isOpen={isDepartureModalOpen}
        onClose={handleClose}
        dismissible
        primaryActionText={primaryActionText}
        isPrimaryActionDisabled={!selectedDeparture || departures.length === 0 || isStreaming}
        onPrimaryActionClick={handleAddSelectedDeparture}
        secondaryActionText={cancelLabel}
        onSecondaryActionClick={handleClose}
      >
        <ChatCruiseDepartureModalContent
          departures={departures}
          expandedDepartureId={expandedDepartureId}
          formatItineraryDayLabel={formatItineraryDayLabel}
          isStreaming={isStreaming}
          itineraryItems={itineraryItems}
          labels={modalLabels}
          onDepartureClick={handleDepartureClick}
          onItineraryToggle={handleItineraryToggle}
          selectedDepartureId={selectedDepartureId}
          tripDatesSummary={tripDatesSummary}
        />
      </Modal>
    </>
  )
}

export default memo(ChatCruiseAddToTripAction)
